import React from 'react';
import { motion } from 'framer-motion';
import { CheckCircle2, Phone, MapPin, Clock, CreditCard, ShoppingCart, HelpCircle, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { TopNav } from '../components/layout/TopNav';
import { BottomNav } from '../components/layout/BottomNav';
import { TrustBadge } from '../components/ui/TrustBadge';
export function WICEBTGuidePage() {
  const wicEligibleItems = ['Fresh fruits and vegetables', 'Whole grains and cereals', 'Milk and dairy products', 'Eggs', 'Peanut butter', 'Beans and legumes', 'Infant formula and baby food', '100% fruit juice'];
  const ebtEligibleItems = ['All fresh produce', 'Meat, poultry, and fish', 'Dairy products', 'Breads and cereals', 'Snack foods', 'Non-alcoholic beverages', 'Seeds and plants for food', 'Most grocery items'];
  const steps = [{
    number: 1,
    title: 'Shop Our Store',
    description: 'Browse our selection of WIC and EBT eligible products. Look for the badges on product cards.'
  }, {
    number: 2,
    title: 'Add to Cart',
    description: "Add your items to cart. We'll show you which items are eligible for your benefits."
  }, {
    number: 3,
    title: 'Checkout',
    description: 'At checkout, select WIC or EBT as your payment method. Present your card at pickup or delivery.'
  }];
  const faqs = [{
    question: 'Can I use WIC and EBT for delivery orders?',
    answer: 'Yes! We accept WIC and EBT for both in-store purchases and delivery orders through our partner apps.'
  }, {
    question: 'How do I know which products are WIC approved?',
    answer: 'Look for the green WIC badge on product cards. You can also filter products by WIC eligibility on our shop page.'
  }, {
    question: "What if my EBT balance doesn't cover my entire order?",
    answer: 'No problem! You can split payment between EBT and another payment method for the remaining balance.'
  }, {
    question: 'Do you accept WIC checks or only eWIC cards?',
    answer: 'We accept both traditional WIC checks and the new eWIC cards. Our staff can help you with either.'
  }];
  return <div className="min-h-screen bg-[#FAF8F5] pb-20 md:pb-0" data-id="element-427">
      <TopNav data-id="element-428" />

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#047857] to-[#059669] text-white py-12 md:py-20" data-id="element-429">
        <div className="max-w-7xl mx-auto px-4 md:px-6" data-id="element-430">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} className="max-w-3xl" data-id="element-431">
            <div className="flex items-center gap-3 mb-4" data-id="element-432">
              <TrustBadge type="WIC" data-id="element-433" />
              <TrustBadge type="EBT" data-id="element-434" />
            </div>
            <h1 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-435">
              WIC & EBT Benefits Guide
            </h1>
            <p className="text-lg md:text-xl text-white/90 mb-8" data-id="element-436">
              Naanfe Market proudly accepts WIC and EBT/SNAP benefits. Learn how
              to use your benefits to shop for authentic African and Caribbean
              groceries.
            </p>
            <Link to="/search?filter=wic" className="inline-flex items-center gap-2 bg-white text-[#047857] px-6 py-3 rounded-xl font-bold hover:bg-gray-100 transition-colors" data-id="element-437">
              Shop WIC Eligible Items
              <ArrowRight className="w-5 h-5" data-id="element-438" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* How It Works */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16" data-id="element-439">
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-440">
          How to Use Your Benefits
        </h2>
        <div className="grid md:grid-cols-3 gap-6" data-id="element-441">
          {steps.map((step, index) => <motion.div key={step.number} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.1
        }} className="bg-white rounded-2xl p-6 shadow-sm border border-gray-100" data-id="element-442">
              <div className="w-12 h-12 bg-[#10b981] text-white rounded-xl flex items-center justify-center text-xl font-bold mb-4" data-id="element-443">
                {step.number}
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2" data-id="element-444">
                {step.title}
              </h3>
              <p className="text-gray-600" data-id="element-445">{step.description}</p>
            </motion.div>)}
        </div>
      </section>

      {/* Eligible Items */}
      <section className="bg-white py-12 md:py-16" data-id="element-446">
        <div className="max-w-7xl mx-auto px-4 md:px-6" data-id="element-447">
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-448">
            What Can You Buy?
          </h2>
          <div className="grid md:grid-cols-2 gap-8" data-id="element-449">
            {/* WIC Items */}
            <motion.div initial={{
            opacity: 0,
            x: -20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-gradient-to-br from-[#047857]/5 to-[#047857]/10 rounded-2xl p-6 md:p-8" data-id="element-450">
              <div className="flex items-center gap-3 mb-6" data-id="element-451">
                <TrustBadge type="WIC" data-id="element-452" />
                <h3 className="text-xl font-bold text-gray-900" data-id="element-453">
                  WIC Eligible Items
                </h3>
              </div>
              <ul className="space-y-3" data-id="element-454">
                {wicEligibleItems.map(item => <li key={item} className="flex items-center gap-3" data-id="element-455">
                    <CheckCircle2 className="w-5 h-5 text-[#047857] flex-shrink-0" data-id="element-456" />
                    <span className="text-gray-700" data-id="element-457">{item}</span>
                  </li>)}
              </ul>
              <Link to="/search?filter=wic" className="mt-6 inline-flex items-center gap-2 text-[#047857] font-semibold hover:underline" data-id="element-458">
                Browse WIC Products
                <ArrowRight className="w-4 h-4" data-id="element-459" />
              </Link>
            </motion.div>

            {/* EBT Items */}
            <motion.div initial={{
            opacity: 0,
            x: 20
          }} whileInView={{
            opacity: 1,
            x: 0
          }} viewport={{
            once: true
          }} className="bg-gradient-to-br from-[#1E3A5F]/5 to-[#1E3A5F]/10 rounded-2xl p-6 md:p-8" data-id="element-460">
              <div className="flex items-center gap-3 mb-6" data-id="element-461">
                <TrustBadge type="EBT" data-id="element-462" />
                <h3 className="text-xl font-bold text-gray-900" data-id="element-463">
                  EBT/SNAP Eligible Items
                </h3>
              </div>
              <ul className="space-y-3" data-id="element-464">
                {ebtEligibleItems.map(item => <li key={item} className="flex items-center gap-3" data-id="element-465">
                    <CheckCircle2 className="w-5 h-5 text-[#1E3A5F] flex-shrink-0" data-id="element-466" />
                    <span className="text-gray-700" data-id="element-467">{item}</span>
                  </li>)}
              </ul>
              <Link to="/search?filter=ebt" className="mt-6 inline-flex items-center gap-2 text-[#1E3A5F] font-semibold hover:underline" data-id="element-468">
                Browse EBT Products
                <ArrowRight className="w-4 h-4" data-id="element-469" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="max-w-7xl mx-auto px-4 md:px-6 py-12 md:py-16" data-id="element-470">
        <div className="flex items-center gap-3 justify-center mb-8" data-id="element-471">
          <HelpCircle className="w-8 h-8 text-[#D97757]" data-id="element-472" />
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900" data-id="element-473">
            Frequently Asked Questions
          </h2>
        </div>
        <div className="max-w-3xl mx-auto space-y-4" data-id="element-474">
          {faqs.map((faq, index) => <motion.div key={index} initial={{
          opacity: 0,
          y: 10
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          delay: index * 0.05
        }} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100" data-id="element-475">
              <h3 className="font-bold text-gray-900 mb-2" data-id="element-476">{faq.question}</h3>
              <p className="text-gray-600" data-id="element-477">{faq.answer}</p>
            </motion.div>)}
        </div>
      </section>

      {/* Contact Section */}
      <section className="bg-[#1E3A5F] text-white py-12 md:py-16" data-id="element-478">
        <div className="max-w-7xl mx-auto px-4 md:px-6" data-id="element-479">
          <div className="text-center mb-8" data-id="element-480">
            <h2 className="text-2xl md:text-3xl font-bold mb-2" data-id="element-481">Need Help?</h2>
            <p className="text-white/80" data-id="element-482">
              Our friendly staff is here to assist you with WIC and EBT
              purchases
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto" data-id="element-483">
            <div className="bg-white/10 rounded-xl p-6 text-center" data-id="element-484">
              <Phone className="w-8 h-8 text-[#F4A261] mx-auto mb-3" data-id="element-485" />
              <h3 className="font-bold mb-1" data-id="element-486">Call Us</h3>
              <a href="tel:2403897863" className="text-[#F4A261] hover:underline" data-id="element-487">
                (240) 389-7863
              </a>
            </div>
            <div className="bg-white/10 rounded-xl p-6 text-center" data-id="element-488">
              <MapPin className="w-8 h-8 text-[#F4A261] mx-auto mb-3" data-id="element-489" />
              <h3 className="font-bold mb-1" data-id="element-490">Visit Us</h3>
              <p className="text-white/80 text-sm" data-id="element-491">
                13535 Clopper Rd
                <br data-id="element-492" />
                Germantown, MD 20874
              </p>
            </div>
            <div className="bg-white/10 rounded-xl p-6 text-center" data-id="element-493">
              <Clock className="w-8 h-8 text-[#F4A261] mx-auto mb-3" data-id="element-494" />
              <h3 className="font-bold mb-1" data-id="element-495">Store Hours</h3>
              <p className="text-white/80 text-sm" data-id="element-496">
                Mon-Sat: 9am-9pm
                <br data-id="element-497" />
                Sunday: Closed
              </p>
            </div>
          </div>
        </div>
      </section>

      <BottomNav data-id="element-498" />
    </div>;
}